<template>
  <div class="warehouse-transfer-attention-view pa-4">
    <!-- 1. Header / Tarjeta de Título Neutra -->
    <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="d-flex align-center justify-space-between py-4 px-5 flex-wrap">
        <div class="d-flex align-center">
          <v-icon color="primary" large class="mr-3">mdi-warehouse</v-icon>
          <div>
            <h2 class="text-h6 font-weight-black grey--text text--darken-4 mb-0">
              Bandeja de Atención de Transferencias
            </h2>
            <span class="text-caption grey--text text--darken-2">
              Gestión de despacho entre almacenes e integración directa con SAP Business One (Service Layer).
            </span>
          </div>
        </div>

        <v-btn
          color="primary"
          outlined
          class="rounded-lg font-weight-bold text-none"
          @click="loadData"
          :loading="loading"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Actualizar Bandeja
        </v-btn>
      </v-card-title>
    </v-card>

    <!-- 2. Tarjeta con Pestañas y Filtros -->
    <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
      <v-tabs v-model="activeTab" color="primary" class="px-3" @change="onTabChange">
        <v-tab class="text-none font-weight-bold">
          <v-badge
            :content="pendingCount.toString()"
            :value="pendingCount > 0"
            color="warning"
            offset-x="-2"
            offset-y="0"
          >
            <v-icon left small>mdi-clock-outline</v-icon>
            Pendientes de Atención
          </v-badge>
        </v-tab>
        <v-tab class="text-none font-weight-bold">
          <v-icon left small>mdi-history</v-icon>
          Historial Diario de Transferencias
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <!-- Filtros de Búsqueda -->
      <v-card-text class="py-4 px-5">
        <v-row dense align="center">
          <v-col cols="12" sm="6" md="3">
            <s-select-area label="Área Solicitante" v-model="filters.AreID" full clearable />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <s-date label="Fecha Inicio" v-model="filters.BeginDate" clearable />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <s-date label="Fecha Fin" v-model="filters.EndDate" clearable />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <s-select-definition :def="1062" label="Turno" v-model="filters.TypeShift" clearable />
          </v-col>
          <v-col cols="12" sm="6" md="2" v-if="activeTab === 1">
            <s-select
              label="Estado"
              :items="historyStatusOptions"
              item-text="text"
              item-value="value"
              v-model="filters.WtrStatus"
              clearable
            />
          </v-col>
          <v-col cols="12" sm="12" md="1" class="d-flex align-center">
            <v-btn
              block
              color="primary"
              class="rounded-lg font-weight-bold text-none"
              elevation="0"
              :loading="loading"
              @click="loadData"
            >
              <v-icon left small>mdi-magnify</v-icon>
              Buscar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 3. Grilla de Datos -->
    <v-card class="rounded-xl border-light" outlined elevation="0">
      <v-data-table
        :headers="computedHeaders"
        :items="items"
        :loading="loading"
        dense
        class="elevation-0"
        no-data-text="No hay transferencias para los filtros seleccionados"
      >
        <template v-slot:[`item.WtrDate`]="{ item }">
          {{ formatDate(item.WtrDate) }}
        </template>
        <template v-slot:[`item.WtrStatus`]="{ item }">
          <v-chip x-small :color="getStatusColor(item.WtrStatus)" class="font-weight-bold" label>
            {{ item.WtrStatusName }}
          </v-chip>
        </template>
        <template v-slot:[`item.WtrSapDocEntry`]="{ item }">
          <span v-if="item.WtrSapDocEntry" class="font-weight-bold text-primary">
            {{ item.WtrSapDocEntry }}
          </span>
          <span v-else class="grey--text">-</span>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <!-- Acción para Pendientes: Atender -->
          <v-btn
            v-if="item.WtrStatus === 1"
            color="primary"
            x-small
            class="text-none font-weight-bold rounded-lg"
            elevation="0"
            @click="openAttentionModal(item)"
          >
            <v-icon left x-small>mdi-check-circle-outline</v-icon>
            Atender
          </v-btn>

          <!-- Acción para Historial: Ver Detalle -->
          <v-tooltip bottom v-else>
            <template v-slot:activator="{ on }">
              <v-btn icon small color="grey darken-2" v-on="on" @click="openViewModal(item)">
                <v-icon small>mdi-file-eye-outline</v-icon>
              </v-btn>
            </template>
            <span>Ver Registro</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modal de Atención con Despacho a SAP -->
    <warehouse-transfer-attention-modal
      v-model="dialogAttention"
      :transfer="selectedTransfer"
      :warehouses="warehouses"
      @attended="loadData"
    />

    <!-- Diálogo de Solo Lectura para Historial -->
    <v-dialog v-model="dialogHistoryDetail" max-width="850" persistent>
      <v-card class="rounded-xl border-light" outlined elevation="0">
        <v-card-title class="d-flex align-center justify-space-between py-3 px-5 border-light-bottom">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-history</v-icon>
            <span class="text-subtitle-1 font-weight-bold grey--text text--darken-4">
              Registro de Transferencia: {{ selectedTransfer ? selectedTransfer.WtrDocumentNumber : '' }}
            </span>
          </div>
          <v-btn icon small @click="dialogHistoryDetail = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="py-4 px-5">
          <v-row dense class="mb-3">
            <v-col cols="12" sm="4">
              <span class="text-caption grey--text text--darken-2">Área Solicitante:</span>
              <div class="font-weight-bold">{{ selectedTransfer ? selectedTransfer.AreName : '-' }}</div>
            </v-col>
            <v-col cols="12" sm="4">
              <span class="text-caption grey--text text--darken-2">Fecha:</span>
              <div class="font-weight-bold">{{ selectedTransfer ? formatDate(selectedTransfer.WtrDate) : '-' }}</div>
            </v-col>
            <v-col cols="12" sm="4">
              <span class="text-caption grey--text text--darken-2">Turno:</span>
              <div class="font-weight-bold">{{ selectedTransfer ? selectedTransfer.TypeShiftName : '-' }}</div>
            </v-col>
            <v-col cols="12" sm="4">
              <span class="text-caption grey--text text--darken-2">Estado:</span>
              <div>
                <v-chip x-small :color="getStatusColor(selectedTransfer ? selectedTransfer.WtrStatus : 0)" label>
                  {{ selectedTransfer ? selectedTransfer.WtrStatusName : '-' }}
                </v-chip>
              </div>
            </v-col>
            <v-col cols="12" sm="4" v-if="selectedTransfer && selectedTransfer.FromWhsCode">
              <span class="text-caption grey--text text--darken-2">Almacén de Origen:</span>
              <div class="font-weight-bold">{{ getWarehouseName(selectedTransfer.FromWhsCode) }}</div>
            </v-col>
            <v-col cols="12" sm="4" v-if="selectedTransfer && selectedTransfer.ToWhsCode">
              <span class="text-caption grey--text text--darken-2">Almacén de Destino:</span>
              <div class="font-weight-bold">{{ getWarehouseName(selectedTransfer.ToWhsCode) }}</div>
            </v-col>
            <v-col cols="12" sm="4" v-if="selectedTransfer && selectedTransfer.WtrSapDocEntry">
              <span class="text-caption grey--text text--darken-2">DocNum SAP:</span>
              <div class="font-weight-bold text-primary">{{ selectedTransfer.WtrSapDocEntry }}</div>
            </v-col>
            <v-col cols="12" class="mt-2" v-if="selectedTransfer && selectedTransfer.WtrCommentsAttention">
              <span class="text-caption grey--text text--darken-2">Comentarios de Despacho:</span>
              <div class="font-italic grey--text text--darken-3">{{ selectedTransfer.WtrCommentsAttention }}</div>
            </v-col>
            <v-col cols="12" class="mt-2" v-if="selectedTransfer && selectedTransfer.WtrReasonRejected">
              <span class="text-caption grey--text text--darken-2">Motivo de Rechazo:</span>
              <div class="error--text font-weight-bold">{{ selectedTransfer.WtrReasonRejected }}</div>
            </v-col>
          </v-row>

          <v-data-table
            :headers="detailHeaders"
            :items="historyDetails"
            :loading="loadingHistoryDetail"
            dense
            hide-default-footer
            :items-per-page="-1"
            class="border-light rounded-lg"
          >
            <template v-slot:[`item.WtdQuantityRequested`]="{ item }">
              {{ Number(item.WtdQuantityRequested).toFixed(2) }}
            </template>
            <template v-slot:[`item.WtdQuantityServed`]="{ item }">
              <span v-if="item.WtdQuantityServed != null" class="font-weight-bold success--text">
                {{ Number(item.WtdQuantityServed).toFixed(2) }}
              </span>
              <span v-else class="grey--text">-</span>
            </template>
            <template v-slot:[`item.FromWhsCode`]="{ item }">
              <span>{{ getWarehouseName(item.FromWhsCode) }}</span>
            </template>
            <template v-slot:[`item.ToWhsCode`]="{ item }">
              <span class="font-weight-medium">{{ getWarehouseName(item.ToWhsCode) }}</span>
            </template>
          </v-data-table>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="py-3 px-5">
          <v-spacer></v-spacer>
          <v-btn text class="text-none rounded-lg" @click="dialogHistoryDetail = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import SSelectArea from "@/components/Utils/SSelectArea.vue";
import SDate from "@/components/Utils/SDate.vue";
import SSelectDefinition from "@/components/Utils/SSelectDefinition.vue";
import SSelect from "@/components/Utils/SSelect.vue";
import WarehouseTransferAttentionModal from "./WarehouseTransferAttentionModal.vue";
import _sWarehouseTransfer from "@/services/Logistics/WarehouseTransferService";

export default {
  name: "WarehouseTransferAttention",
  components: {
    SSelectArea,
    SDate,
    SSelectDefinition,
    SSelect,
    WarehouseTransferAttentionModal,
  },
  data() {
    return {
      activeTab: 0,
      loading: false,
      dialogAttention: false,
      dialogHistoryDetail: false,
      loadingHistoryDetail: false,
      selectedTransfer: null,
      historyDetails: [],
      warehouses: [],
      pendingCount: 0,
      filters: {
        AreID: null,
        BeginDate: null,
        EndDate: null,
        TypeShift: null,
        WtrStatus: 1, // Pendiente por defecto en la pestaña 0
        Search: "",
      },
      historyStatusOptions: [
        { text: "TODOS", value: null },
        { text: "PENDIENTE", value: 1 },
        { text: "ATENDIDO", value: 2 },
        { text: "RECHAZADO", value: 3 },
      ],
      items: [],
      headers: [
        { text: "N° Solicitud", value: "WtrDocumentNumber", width: 130, sortable: true },
        { text: "Fecha", value: "WtrDate", width: 110, sortable: true },
        { text: "Área Solicitante", value: "AreName", sortable: true },
        { text: "Turno", value: "TypeShiftName", width: 110, sortable: true },
        { text: "Encargado", value: "WtrManager", sortable: true },
        { text: "Ítems", value: "TotalItems", width: 80, align: "center", sortable: true },
        { text: "Estado", value: "WtrStatus", width: 120, align: "center", sortable: true },
        { text: "DocNum SAP", value: "WtrSapDocEntry", width: 120, align: "center", sortable: true },
        { text: "Acciones", value: "actions", width: 110, align: "center", sortable: false },
      ],
      detailHeaders: [
        { text: "Línea", value: "WtdNumLine", width: 70, align: "center" },
        { text: "Código", value: "ArtCode", width: 110 },
        { text: "Descripción del Material", value: "WtdDescription" },
        { text: "U.M.", value: "UnitMeasurement", width: 70, align: "center" },
        { text: "Cant. Solicitada", value: "WtdQuantityRequested", width: 130, align: "right" },
        { text: "Cant. Despachada", value: "WtdQuantityServed", width: 130, align: "right" },
        { text: "Almacén Origen", value: "FromWhsCode", width: 160 },
        { text: "Almacén Destino", value: "ToWhsCode", width: 160 },
      ],
    };
  },
  computed: {
    computedHeaders() {
      return this.headers;
    },
  },
  created() {
    this.loadWarehouses();
    this.loadData();
  },
  methods: {
    loadWarehouses() {
      _sWarehouseTransfer.getWarehouses().then((resp) => {
        this.warehouses = resp.data || [];
      });
    },
    onTabChange(tab) {
      if (tab === 0) {
        // Pestaña Pendientes
        this.filters.WtrStatus = 1;
      } else {
        // Pestaña Historial Diario
        this.filters.WtrStatus = null;
      }
      this.loadData();
    },
    loadData() {
      this.loading = true;
      _sWarehouseTransfer
        .list(this.filters, this.$fun.getUserID())
        .then((resp) => {
          this.items = resp.data || [];
          if (this.activeTab === 0) {
            this.pendingCount = this.items.length;
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    openAttentionModal(item) {
      this.selectedTransfer = item;
      this.dialogAttention = true;
    },
    openViewModal(item) {
      this.selectedTransfer = item;
      this.dialogHistoryDetail = true;
      this.loadingHistoryDetail = true;
      _sWarehouseTransfer
        .detail({ WtrID: item.WtrID }, this.$fun.getUserID())
        .then((resp) => {
          this.historyDetails = resp.data || [];
          this.loadingHistoryDetail = false;
        })
        .catch(() => {
          this.loadingHistoryDetail = false;
        });
    },
    getStatusColor(status) {
      switch (status) {
        case 1:
          return "warning"; // Pendiente
        case 2:
          return "success"; // Atendido
        case 3:
          return "error"; // Rechazado
        default:
          return "grey";
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      return dateStr.substring(0, 10);
    },
    getWarehouseName(code) {
      if (!code) return "-";
      const strCode = String(code).trim();
      const wh = this.warehouses.find(
        (w) =>
          String(w.WhsCode).trim() === strCode ||
          String(w.WhsCode).trim().padStart(2, "0") === strCode.padStart(2, "0")
      );
      return wh ? `${wh.WhsCode} - ${wh.WhsName}` : code;
    },
  },
};
</script>

